import type { QueryClient, QueryObserverOptions } from "@tanstack/react-query";
import type { Getter } from "jotai";
import { atomWithQuery } from "jotai-tanstack-query";

import {
  shouldRetryBackendRead,
  unwrapBackendResult,
  type BackendFailure,
  type BackendFailureError,
} from "#/shared/lib/backend-api";
import type { ResultAsync } from "#/shared/lib/result";

import type {
  ResourceDefinition,
  ResourceQueryKey,
  ResourceQueryReference,
} from "./definition";

type ResourceQueryOptions<TData, TError> = Pick<
  QueryObserverOptions<TData, TError, TData, TData>,
  "refetchInterval" | "refetchOnMount" | "refetchOnWindowFocus" | "retry"
> & {
  staleTime?: number;
};

export interface ResourceQueryDefinition<
  TInput,
  TData,
  TError,
> extends ResourceQueryReference {
  getKey: (input: TInput) => ResourceQueryKey;
  getOptions: (input: TInput) => ResourceQueryOptions<TData, TError>;
  queryFn: (input: TInput) => Promise<TData>;
}

interface ResourceQueryConfig<
  TQueryName extends string,
  TInput,
  TData,
> extends ResourceQueryOptions<TData, BackendFailureError> {
  key: (input: TInput) => readonly unknown[];
  name: TQueryName;
  queryFn: (input: TInput) => ResultAsync<TData, BackendFailure>;
  resource: ResourceDefinition<string>;
}

/**
 * 读契约的唯一定义入口：query key、缓存策略、失败重试都收在这里，
 * 调用方只拿到 atom，不自己拼 key，也不自己写重试策略。
 */
export function defineResourceQuery<
  const TQueryName extends string,
  TInput,
  TData,
>(
  definition: ResourceQueryConfig<TQueryName, TInput, TData>,
): ResourceQueryDefinition<TInput, TData, BackendFailureError> {
  const { key, name, queryFn, resource, ...options } = definition;
  const scopeKey = [resource.name, name] as const;

  assertValidResourceStaleTime(options.staleTime);

  return {
    getKey: (input) => [...scopeKey, ...key(input)] as const,
    getOptions: () => ({ retry: shouldRetryBackendRead, ...options }),
    queryFn: (input) => unwrapBackendResult(queryFn(input)),
    scopeKey,
  };
}

function assertValidResourceStaleTime(staleTime: number | undefined): void {
  if (
    staleTime !== undefined &&
    (!Number.isFinite(staleTime) || staleTime < 0)
  ) {
    throw new Error("Resource query staleTime 必须是非负有限值");
  }
}

export function createResourceQueryAtom<TInput, TData, TError>(
  query: ResourceQueryDefinition<TInput, TData, TError>,
  readInput: (get: Getter) => TInput,
  getQueryClient?: (get: Getter) => QueryClient,
) {
  return atomWithQuery<TData, TError>((get) => {
    const input = readInput(get);

    return {
      ...query.getOptions(input),
      queryFn: () => query.queryFn(input),
      queryKey: query.getKey(input),
    };
  }, getQueryClient);
}
